"use client";

import { cn } from "@/lib/utils";
import { useState, type ReactNode } from "react";

export type BarListRow = {
  key: string;
  label: ReactNode;
  value: number;
  display?: string;
  onClick?: () => void;
};

export function BarList({
  rows,
  empty,
  accentFirst = true,
  className,
}: {
  rows: BarListRow[];
  empty?: ReactNode;
  accentFirst?: boolean;
  className?: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(1, ...rows.map((row) => row.value));

  if (rows.length === 0) {
    return <p className="text-[13px] text-muted-foreground">{empty}</p>;
  }

  return (
    <ul className={cn("space-y-2.5", className)}>
      {rows.map((row, i) => {
        const content = (
          <>
            <div className="flex items-baseline justify-between gap-2 text-[13px]">
              <span className="min-w-0 truncate">{row.label}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {row.display ?? row.value}
              </span>
            </div>
            <div className="mt-1 h-2 rounded-[3px] bg-muted">
              <div
                className={cn(
                  "h-full rounded-[3px] transition-[width] duration-500",
                  accentFirst && i === 0 ? "bg-primary" : "bg-foreground/70",
                )}
                style={{ width: `${(row.value / max) * 100}%` }}
              />
            </div>
          </>
        );
        return (
          <li
            key={row.key}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            className={cn(
              "transition-opacity",
              hovered !== null && hovered !== i && "opacity-40",
            )}
          >
            {row.onClick ? (
              <button type="button" onClick={row.onClick} className="block w-full text-left">
                {content}
              </button>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ul>
  );
}
